/**
 * styles.ts — CSS/style handling for compiled components.
 *
 * Extracted from signals.ts. Self-contained: no reactive dependencies.
 * Handles style object application, px suffixing for numeric values,
 * and diffing against _prevStyles for efficient updates.
 */
// CSS properties that don't need 'px' suffix for numeric values.
// Stored as a comma-joined string and split at init to save ~80 bytes after minification
// vs individual quoted strings in a Set literal.
export const CSS_UNITLESS_PROPS = new Set('animationIterationCount,aspectRatio,borderImageOutset,borderImageSlice,borderImageWidth,boxFlex,boxFlexGroup,boxOrdinalGroup,columnCount,columns,flex,flexGrow,flexPositive,flexShrink,flexNegative,flexOrder,gridArea,gridRow,gridRowEnd,gridRowSpan,gridRowStart,gridColumn,gridColumnEnd,gridColumnSpan,gridColumnStart,fontWeight,lineClamp,lineHeight,opacity,order,orphans,scale,tabSize,widows,zIndex,zoom,fillOpacity,floodOpacity,stopOpacity,strokeDasharray,strokeDashoffset,strokeMiterlimit,strokeOpacity,strokeWidth'.split(','));
// Global slot used by keyedList to pass previous style state to applyStyles
// when re-rendering an existing keyed item. This allows applyStyles to diff
// against the old styles and only update changed properties (matching React's
// reconciler behavior for CSS shorthand/longhand ordering).
export var _pendingPrevStyles = null;
/** Set the pending prev styles map (called by keyedList before re-rendering). */
export function setPendingPrevStyles(val) {
    _pendingPrevStyles = val;
}
function _applyStyleProp(el, prop, val) {
    // React converts 'float' to 'cssFloat' (float is a reserved word in JS)
    if (prop === 'float')
        prop = 'cssFloat';
    if (prop.startsWith('--')) {
        if (val == null || typeof val === 'boolean') {
            el.style.removeProperty(prop);
        }
        else {
            el.style.setProperty(prop, val);
        }
    }
    else if (typeof val === 'number' && val !== 0 && !CSS_UNITLESS_PROPS.has(prop)) {
        el.style[prop] = val + 'px';
    }
    else if (val == null || typeof val === 'boolean') {
        el.style[prop] = '';
    }
    else {
        el.style[prop] = typeof val === 'number' ? String(val) : val;
    }
}
/**
 * applyStyles — properly applies a style object to a DOM element,
 * converting numeric values to 'px' strings for non-unitless properties
 * (matching React's behavior).
 *
 * When previous style state is available (via el._prevStyles or the global
 * _pendingPrevStyles from keyedList), reproduces React's style reconciliation:
 * first applies the old styles to establish the base CSS state, then only
 * applies changed properties. This prevents shorthand properties like `border`
 * from overriding longhands like `borderBottom` when only the longhand changed.
 */
export function applyStyles(el, styles) {
    if (!styles || typeof styles !== 'object')
        return;
    // Check for style state transferred from a previous keyed list render
    var prev = el._prevStyles;
    if (!prev && _pendingPrevStyles) {
        var matchKey = el.tagName + (el.id ? '#' + el.id : '');
        prev = _pendingPrevStyles.get(matchKey) || null;
    }
    if (prev) {
        // Reproduce React's reconciler behavior on a fresh element:
        // 1) Apply ALL old properties in their original key order.
        //    This reproduces the cssText state from the previous render.
        for (var prop in prev) {
            _applyStyleProp(el, prop, prev[prop]);
        }
        // 2) Apply only CHANGED properties from the new style object.
        for (var prop in styles) {
            if (prev[prop] !== styles[prop]) {
                _applyStyleProp(el, prop, styles[prop]);
            }
        }
        // 3) Remove properties that were in prev but not in new styles.
        for (var oldProp in prev) {
            if (!(oldProp in styles)) {
                if (oldProp.startsWith('--')) {
                    el.style.removeProperty(oldProp);
                }
                else {
                    el.style[oldProp] = '';
                }
            }
        }
    }
    else {
        // First application — apply all in object key order (matching React initial render)
        for (var prop in styles) {
            _applyStyleProp(el, prop, styles[prop]);
        }
    }
    el._prevStyles = styles;
}
